import React, { CSSProperties, SVGProps, useCallback } from "react";
import { SVG_SMART_OBJECT_TYPES, SmartObjectDynamicText, SmartObjectElement } from "../..";
import { SmartObjectSvgHotSpot } from "./HotSpot";
import { SmartObjectSvgRotate } from "./Rotate";
import { useShouldRender } from "./useShouldRender";
import { SmartObjectSvgFillRenderer } from "./Fill";
import { SmartObjectSvgImageSwapRenderer } from "./ImageSwap";
import { useSmartObjectStyles } from "./useSmartObjectStyles";
import { SmartObjectSvgDynamicTextRenderer } from "./DynamicText";
import { cleanSmartObjectText } from "../../utils";
import { SmartObjectSvgImageTextRenderer } from "./ImageText";
import { useDomRef } from "../../../../hooks/useDomRef";
import { useSvgZoomAndDrag } from "./useSvgZoom";
import { ObjectActionsType, useObjectsDispatch, useObjectsState } from "../../../../contexts/ObjectsProvider";
import { useViewBox } from "../../../../hooks/useViewBox";
import { useTimeline } from "../../../../contexts/TimelineProvider/TimelineProvider";
import { zoomRatioToViewBoxZoom } from "../../../../utils";
import { useObjectIsInTime } from "../../../../hooks/useObjectIsInTime";

export interface SmartObjectSvgRendererProps<T extends SmartObjectElement = SmartObjectElement> {
  element: T;
  objectId?: string;
  parentId?: string | undefined;
  extraStyles?: CSSProperties;
  className?: any;
  onClick?: () => void;
}

export interface SmartObjectComponent {
  element: SmartObjectElement;
  objectId?: string;
  parentId?: string | undefined;
  extraStyles?: CSSProperties;
  className?: any;
  onClick?: () => void;
}

export function SmartObjectSvgRenderer({
  element,
  objectId,
  parentId,
  extraStyles,
  onClick,
}: SmartObjectSvgRendererProps) {
  const { type, GROUP_ID, TYPE } = element;
  const uniqueKey = `${parentId}-${objectId}-${type}-${GROUP_ID}`;

  const svgId = GROUP_ID ?? parentId ?? undefined;

  if (TYPE === SVG_SMART_OBJECT_TYPES.HOTSPOT) {
    return <SmartObjectSvgHotSpot key={uniqueKey} element={element} objectId={objectId} parentId={parentId} />;
  }
  if (TYPE === SVG_SMART_OBJECT_TYPES.ROTATE) {
    return <SmartObjectSvgRotate key={uniqueKey} element={element} objectId={objectId} parentId={parentId} />;
  }

  if (TYPE === SVG_SMART_OBJECT_TYPES.FILL_VALUE) {
    return <SmartObjectSvgFillRenderer key={uniqueKey} element={element} objectId={objectId} parentId={parentId} />;
  }

  if (TYPE === SVG_SMART_OBJECT_TYPES.IMAGE_SWAP) {
    return (
      <SmartObjectSvgImageSwapRenderer key={uniqueKey} element={element} objectId={objectId} parentId={parentId} />
    );
  }

  if (TYPE === SVG_SMART_OBJECT_TYPES.DYNAMIC_TEXT) {
    return (
      <SmartObjectSvgDynamicTextRenderer
        key={uniqueKey}
        element={element as SmartObjectDynamicText}
        objectId={objectId}
        parentId={parentId}
      />
    );
  }

  if (TYPE === SVG_SMART_OBJECT_TYPES.IMAGE_TEXT) {
    return (
      <SmartObjectSvgImageTextRenderer key={uniqueKey} element={element} objectId={objectId} parentId={parentId} />
    );
  }

  if (TYPE === SVG_SMART_OBJECT_TYPES.FONT) {
    /**
     * We dont want to show the font images in the
     * smart object, they are used in the meta variables store
     */
    return null;
  }

  switch (type) {
    case "svg": {
      return (
        <MemoizedSmartObjectSvgContainer
          key={uniqueKey}
          element={element}
          objectId={objectId}
          parentId={svgId}
          extraStyles={extraStyles}
        />
      );
    }
    case "path": {
      return <SmartObjectSvgPath key={uniqueKey} element={element} objectId={objectId} parentId={svgId} />;
    }
    case "g": {
      return <MemoizedSmartObjectG key={uniqueKey} element={element} objectId={objectId} parentId={svgId} />;
    }
    case "image": {
      return <SmartObjectImage key={uniqueKey} element={element} objectId={objectId} parentId={svgId} />;
    }
    case "defs": {
      return <MemoizedSmartObjectDefs key={uniqueKey} element={element} objectId={objectId} />;
    }
    case "rect": {
      return <SmartObjectRect key={uniqueKey} element={element} extraStyles={extraStyles} />;
    }
    case "circle": {
      return <SmartObjectCircle key={uniqueKey} element={element} />;
    }
    case "polygon": {
      return <SmartObjectPolygon key={uniqueKey} element={element} />;
    }
    case "text": {
      return <MemoizedSmartObjectText key={uniqueKey} element={element} objectId={objectId} />;
    }
    case "tspan": {
      return <SmartObjectTSpan key={uniqueKey} element={element} />;
    }
    case "polyline": {
      return <SmartObjectPolyline key={uniqueKey} element={element} />;
    }
    case "line": {
      return <SmartObjectLine key={uniqueKey} element={element} objectId={objectId} />;
    }
    case "style": {
      if (element.styles) {
        return <style>{element.styles}</style>;
      }
      return null;
    }
    case "use": {
      return <SmartObjectUse key={uniqueKey} element={element} objectId={objectId} parentId={svgId} />;
    }
    case "linearGradient": {
      return <SmartObjectLinearGradient key={uniqueKey} element={element} objectId={objectId} />;
    }
    default: {
      console.warn("SmartObjectSvgRenderer: type not handled", type);
      return null;
    }
  }
}
export const MemoizedSmartObjectSvgRenderer = React.memo(SmartObjectSvgRenderer);

export function SmartObjectSvgContainer({ element, objectId, extraStyles }: SmartObjectComponent) {
  const { children, ...rest } = element;
  const svgProps = rest as SVGProps<SVGSVGElement>;
  const [ref, setRef] = useDomRef<SVGSVGElement>();
  const objectsDispatch = useObjectsDispatch();
  const objectsState = useObjectsState();
  const [tl] = useTimeline();
  const currentTime = tl.scrubbingCurrentTime;
  const objectInTimeline = useObjectIsInTime(objectId);
  const object = objectsState.smartObjects.find((obj) => obj.objectId === objectId);
  const hasZoom = object?.viewBox !== undefined ? true : false;
  const { viewBox, viewBoxZoom, viewBoxX, viewBoxY } = useViewBox(objectId);

  const onViewBoxChange = useCallback<NonNullable<Parameters<typeof useSvgZoomAndDrag>[0]["onViewBoxChange"]>>(
    ({ x, y, ratio, viewBox }) => {
      if (!objectId) return;
      if (objectInTimeline) {
        // create a frame with the new pitch value
        const newFrame: {
          timestamp: number;
          viewBox: string;
          viewBoxX?: number;
          viewBoxY?: number;
          viewBoxZoom?: number;
        } = {
          timestamp: currentTime,
          viewBox,
        };
        if (typeof x === "number") newFrame.viewBoxX = x;
        if (typeof y === "number") newFrame.viewBoxY = y;
        if (typeof ratio === "number") newFrame.viewBoxZoom = zoomRatioToViewBoxZoom(ratio);
        else {
          newFrame.viewBoxZoom = viewBoxZoom;
        }
        objectsDispatch({
          type: ObjectActionsType.UPSERT_OBJECT_FRAME,
          payload: {
            objectId: objectId,
            frame: newFrame,
          },
        });
      }

      objectsDispatch({
        type: ObjectActionsType.SET_VIEW_BOX,
        payload: { objectId, x, y, ratio },
      });
    },
    [currentTime, objectInTimeline, viewBoxZoom],
  );
  useSvgZoomAndDrag({
    svgRef: ref,
    startingViewBox: svgProps.viewBox,
    onViewBoxChange,
  });

  return (
    <svg
      width="100%"
      height="100%"
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      aria-label="Smart Object Svg"
      {...svgProps}
      style={extraStyles}
      ref={setRef}
      viewBox={hasZoom ? viewBox : svgProps.viewBox}
    >
      {children?.map((item, index) => (
        <MemoizedSmartObjectSvgRenderer
          element={item}
          key={`${objectId}-svg-child-${item.id}-${index}`}
          objectId={objectId}
        />
      ))}
    </svg>
  );
}
export const MemoizedSmartObjectSvgContainer = React.memo(SmartObjectSvgContainer);

export function SmartObjectSvgPath({ element, extraStyles }: SmartObjectComponent) {
  const { children, id, type, ...rest } = element;
  const style = useSmartObjectStyles(element.style, extraStyles);

  const props = rest as SVGProps<SVGPathElement>;

  return <path {...props} style={style} />;
}

export function SmartObjectG({ element, objectId, onClick }: SmartObjectComponent) {
  const { children, TYPE, GROUP_ID, VALUE, HOTSPOT_ID, RATE, ...rest } = element;
  const style = useSmartObjectStyles(element.style);

  const props = rest as SVGProps<SVGGElement>;

  return (
    <g {...props} style={style} onClick={onClick}>
      {children?.map((item, index) => (
        <MemoizedSmartObjectSvgRenderer key={`${objectId}-group-child-${index}`} element={item} objectId={objectId} />
      ))}
    </g>
  );
}
export const MemoizedSmartObjectG = React.memo(SmartObjectG);

export function SmartObjectImage({ element, objectId }: SmartObjectComponent) {
  const { children, TYPE, GROUP_ID, VALUE, ...rest } = element;
  const shouldRender = useShouldRender(objectId, GROUP_ID, TYPE as SVG_SMART_OBJECT_TYPES, VALUE);
  const style = useSmartObjectStyles(element.style);

  if (!shouldRender) return null;

  const imageProps = rest as SVGProps<SVGImageElement> & { "xlink:href": string };

  return <image {...imageProps} xlinkHref={imageProps["xlink:href"]} style={style}></image>;
}

export function SmartObjectUse({ element, objectId, extraStyles, onClick }: SmartObjectComponent) {
  const { children, TYPE, GROUP_ID, VALUE, ...rest } = element;
  const shouldRender = useShouldRender(objectId, GROUP_ID, TYPE as SVG_SMART_OBJECT_TYPES, VALUE);
  const style = useSmartObjectStyles(element.style, extraStyles);

  if (!shouldRender) return null;

  const useProps = rest as SVGProps<SVGUseElement> & { "xlink:href": string };

  return <use {...useProps} style={style} xlinkHref={useProps["xlink:href"]} onClick={onClick}></use>;
}

export function SmartObjectDefs({ element, objectId }: SmartObjectComponent) {
  const { children, ...rest } = element;

  const defsProps = rest as SVGProps<SVGDefsElement>;

  return (
    <defs {...defsProps}>
      {children?.map((item, index) => (
        <MemoizedSmartObjectSvgRenderer key={`${objectId}-defs-child-${index}`} element={item} objectId={objectId} />
      ))}
    </defs>
  );
}
export const MemoizedSmartObjectDefs = React.memo(SmartObjectDefs);

export function SmartObjectRect({ element, extraStyles }: SmartObjectComponent) {
  const style = useSmartObjectStyles(element.style, extraStyles);
  const rectProps = element as SVGProps<SVGRectElement>;
  return <rect {...rectProps} style={style} />;
}

export function SmartObjectCircle({ element, extraStyles }: SmartObjectComponent) {
  const circleProps = element as SVGProps<SVGCircleElement>;
  const style = useSmartObjectStyles(element.style, extraStyles);
  return <circle {...circleProps} style={style} />;
}

export function SmartObjectLine({ element, extraStyles }: SmartObjectComponent) {
  const lineProps = element as SVGProps<SVGLineElement>;
  const style = useSmartObjectStyles(element.style, extraStyles);
  return <line {...lineProps} style={style} />;
}

export function SmartObjectPolyline({ element, extraStyles }: SmartObjectComponent) {
  const polylineProps = element as SVGProps<SVGPolylineElement>;
  const style = useSmartObjectStyles(element.style, extraStyles);
  return <polyline {...polylineProps} style={style} />;
}

export function SmartObjectPolygon({ element, extraStyles }: SmartObjectComponent) {
  const polygonProps = element as SVGProps<SVGPolygonElement>;
  const style = useSmartObjectStyles(element.style, extraStyles);
  return <polygon {...polygonProps} style={style} />;
}

export function SmartObjectText({ element, objectId }: SmartObjectComponent) {
  const { children, textContent } = element;
  const textProps = element as SVGProps<SVGTextElement>;
  const style = useSmartObjectStyles(element.style);
  const text = cleanSmartObjectText(textContent);
  return (
    <text {...textProps} style={style}>
      {text}
      {!text &&
        children?.map((item, index) => (
          <MemoizedSmartObjectSvgRenderer key={`${objectId}-text-${index}`} element={item} objectId={objectId} />
        ))}
    </text>
  );
}
export const MemoizedSmartObjectText = React.memo(SmartObjectText);

export function SmartObjectTSpan({ element }: SmartObjectComponent) {
  const { textContent, ...rest } = element;
  const tspanProps = rest as SVGProps<SVGTSpanElement>;
  const style = useSmartObjectStyles(element.style);
  return (
    <tspan {...tspanProps} style={style}>
      {textContent || ""}
    </tspan>
  );
}

export function SmartObjectLinearGradient({ element }: SmartObjectComponent) {
  const { children, ...rest } = element;
  const linearGradientProps = rest as SVGProps<SVGLinearGradientElement>;
  return <linearGradient {...linearGradientProps}></linearGradient>;
}
